import { Helmet } from 'react-helmet';
import { useEffect, useState } from 'react';

interface SEOHeadProps {
  pagePath: string;
  defaultTitle?: string;
  defaultDescription?: string;
  defaultKeywords?: string;
  ogImage?: string;
  /** When true, default this page to noindex (a seo_metadata override still wins). */
  noIndex?: boolean;
  article?: {
    publishedTime?: string;
    author?: string;
  };
}

interface SEOMetadata {
  meta_title: string;
  meta_description: string;
  meta_keywords?: string;
  og_title?: string;
  og_description?: string;
  og_image_url?: string;
  twitter_title?: string;
  twitter_description?: string;
  twitter_image_url?: string;
  canonical_url?: string;
  robots?: string;
}

export const SEOHead = ({
  pagePath,
  defaultTitle,
  defaultDescription,
  defaultKeywords,
  ogImage,
  noIndex,
  article,
}: SEOHeadProps) => {
  const [seoData, setSeoData] = useState<SEOMetadata | null>(null);


  useEffect(() => {
    let cancelled = false;
    const fetchSEOData = async () => {
      try {
        // Loaded on demand so Firebase Firestore SDK stays out of the critical rendering path.
        const [{ db }, { collection, query, where, getDocs, limit }] = await Promise.all([
          import('@/integrations/firebase/client'),
          import('firebase/firestore'),
        ]);

        const q = query(
          collection(db, 'seo_metadata'),
          where('page_path', '==', pagePath),
          where('is_active', '==', true),
          limit(1)
        );
        const snap = await getDocs(q);

        if (!cancelled && !snap.empty) {
          setSeoData(snap.docs[0].data() as SEOMetadata);
        }
      } catch (err) {
        console.error('Error fetching SEO metadata:', err);
      }
    };

    fetchSEOData();
    return () => { cancelled = true; };
  }, [pagePath]);

  const currentUrl = `https://www.ulcworld.com${pagePath}`;
  const canonicalUrl = seoData?.canonical_url || `https://www.ulcworld.com${pagePath}`;
  const title = seoData?.meta_title || defaultTitle || 'ULC World';
  const description = seoData?.meta_description || defaultDescription || 'Study at top ranked universities in the UK';
  const keywords = seoData?.meta_keywords || defaultKeywords || 'UK student visa, Study in the UK, UK university application';
  const ogTitle = seoData?.og_title || title;
  const ogDescription = seoData?.og_description || description;
  const ogImageUrl = seoData?.og_image_url || ogImage || 'https://www.ulcworld.com/og-image.jpg';
  const twitterTitle = seoData?.twitter_title || ogTitle;
  const twitterDescription = seoData?.twitter_description || ogDescription;
  const twitterImageUrl = seoData?.twitter_image_url || ogImageUrl;
  const robots = seoData?.robots || (noIndex ? 'noindex, follow' : 'index, follow');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="ULC World" />
      <meta property="og:locale" content="en_GB" />

      {/* Article specific tags */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={twitterImageUrl} />
      <meta name="twitter:site" content="@ulcworld" />

      {/* Additional SEO Tags */}
      <meta name="author" content="ULC World" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* Favicon */}
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="96x96" href="/favicon-96x96.png" />

      {/* Theme Color */}
      <meta name="theme-color" content="#1a365d" />
      <meta name="msapplication-TileColor" content="#1a365d" />
    </Helmet>
  );
};
